if (!customElements.get('price-reveal-toggle')) {
  customElements.define(
    'price-reveal-toggle',
    class PriceRevealToggle extends HTMLElement {
      constructor() {
        super();
        this.checkbox = this.querySelector('.price-reveal-toggle__checkbox');
        this.offLabel = this.querySelector('[data-price-reveal-toggle-off]');
        this.onLabel = this.querySelector('[data-price-reveal-toggle-on]');
        this.checkbox.addEventListener('change', this.onChange.bind(this));

        // Cache the fake (compare_at_price-only) markup from the price block's own initial
        // render so unchecking can restore it without a second network round-trip. The price
        // block's id (price-{{ section.id }}, see product-block.liquid) is passed explicitly
        // via data-price-slot-id rather than guessed from DOM ancestry.
        const priceBlock = document.getElementById(this.dataset.priceSlotId);
        this.priceSlot = priceBlock?.querySelector('[data-price-reveal-slot]');
        this.fakeMarkup = this.priceSlot ? this.priceSlot.innerHTML : null;

        // The small weight-picker card (variant-card.liquid) for THIS SAME variant, if one is on
        // the page — searched document-wide since it lives in a different section than the
        // toggle/price block. Its price span carries the same fake markup by default, revealed by
        // this same toggle. May legitimately not exist (e.g. no variant-card picker on this PDP).
        this.cardSlot = document.querySelector(
          `[data-price-reveal-card-slot][data-variant-id="${this.dataset.variantId}"]`,
        );
        this.cardFakeMarkup = this.cardSlot ? this.cardSlot.innerHTML : null;

        // That same weight-picker card's own €/kg line — same fake-by-default treatment as the
        // main €/kg line below (unit-price-calc.liquid's fake_when_on_sale).
        this.cardUnitSlot = document.querySelector(
          `[data-price-reveal-card-unit-slot][data-variant-id="${this.dataset.variantId}"]`,
        );
        this.cardUnitFakeMarkup = this.cardUnitSlot ? this.cardUnitSlot.innerHTML : null;

        // The €/kg line below the main price — same fake-by-default treatment (see
        // snippets/price.liquid's fake_unit_price), same reason: the real unit_price alone would
        // let a scraper back-derive the real total price.
        this.unitSlot = priceBlock?.querySelector('[data-price-reveal-unit-slot]');
        this.unitFakeMarkup = this.unitSlot ? this.unitSlot.innerHTML : null;

        // "Melhor em Conjunto" combo card(s) (smart-addon-combo-card.liquid) for THIS SAME
        // variant — may be more than one instance on the page. Each fakes the current product's
        // price via compare_at_price by default, including in its own live quantity-stepper
        // recompute (assets/smart-addons.js, unmodified — reveal just patches the data attribute
        // + re-dispatches the same 'change' event that recompute already listens for). Reveal is
        // one-way for these cards (per product decision): unchecking does not revert them back to
        // the fake total, since re-deriving the exact pre-reveal state after a shopper may have
        // already changed quantities isn't worth the complexity for a secondary display.
        this.comboCards = Array.from(
          document.querySelectorAll(`[data-smart-addon-pair][data-source-variant-id="${this.dataset.variantId}"]`),
        );
      }

      onChange() {
        if (this.checkbox.checked) {
          this.reveal();
        } else {
          this.hide();
        }
      }

      reveal() {
        const slot = this.priceSlot;
        if (!slot) return;

        this.setAttribute('loading', '');

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) rootUrl = rootUrl + '/';
        const variantId = this.dataset.variantId;
        const requestUrl = `${rootUrl}variants/${variantId}/?section_id=price-reveal`;

        fetch(requestUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionHtml = new DOMParser().parseFromString(text, 'text/html');
            const revealedMarkup = sectionHtml.querySelector('[data-price-reveal-markup]');
            if (revealedMarkup) {
              slot.innerHTML = revealedMarkup.innerHTML;
              // The revealed markup is the REAL, genuinely-on-sale price — let the merchant's
              // sale-color setting apply to it again (see component-price.css).
              slot.removeAttribute('data-price-reveal-fake');
            }

            const revealedCardMarkup = sectionHtml.querySelector('[data-price-reveal-card-markup]');
            if (this.cardSlot && revealedCardMarkup) {
              this.cardSlot.innerHTML = revealedCardMarkup.innerHTML;
              this.cardSlot.removeAttribute('data-price-reveal-fake');
            }

            const revealedUnitMarkup = sectionHtml.querySelector('[data-price-reveal-unit-markup]');
            if (this.unitSlot && revealedUnitMarkup) {
              this.unitSlot.innerHTML = revealedUnitMarkup.innerHTML;
            }

            const revealedCardUnitMarkup = sectionHtml.querySelector('[data-price-reveal-card-unit-markup]');
            if (this.cardUnitSlot && revealedCardUnitMarkup) {
              this.cardUnitSlot.innerHTML = revealedCardUnitMarkup.innerHTML;
            }

            const revealedSourceMarkup = sectionHtml.querySelector('[data-price-reveal-source-markup]');
            if (revealedSourceMarkup && this.comboCards.length) {
              const realCents = revealedSourceMarkup.dataset.cents;
              const realText = revealedSourceMarkup.textContent;
              this.comboCards.forEach((card) => {
                card.dataset.sourcePrice = realCents;
                const sample = card.querySelector('[data-smart-addon-price-sample]');
                if (sample) sample.textContent = realText;
                // Re-trigger smart-addons.js's own recompute via the same 'change' listener it
                // already attaches to the source quantity input — no need to duplicate its logic.
                const sourceInput = card.querySelector(
                  '[data-smart-addon-quantity-role="source"] [data-smart-addon-qty-input]',
                );
                if (sourceInput) sourceInput.dispatchEvent(new Event('change', { bubbles: true }));
              });
            }

            this.showActivatedState();
          })
          .catch((error) => {
            console.error(error);
            // Revert the checkbox rather than leave it checked with nothing revealed.
            this.checkbox.checked = false;
          })
          .finally(() => {
            this.removeAttribute('loading');
          });
      }

      hide() {
        if (this.priceSlot && this.fakeMarkup !== null) {
          this.priceSlot.innerHTML = this.fakeMarkup;
          // Back to showing the fake (compare_at_price-only) number — restore the
          // foreground-color override so it doesn't render in the merchant's sale color.
          this.priceSlot.setAttribute('data-price-reveal-fake', '');
        }
        if (this.cardSlot && this.cardFakeMarkup !== null) {
          this.cardSlot.innerHTML = this.cardFakeMarkup;
          this.cardSlot.setAttribute('data-price-reveal-fake', '');
        }
        if (this.unitSlot && this.unitFakeMarkup !== null) {
          this.unitSlot.innerHTML = this.unitFakeMarkup;
        }
        if (this.cardUnitSlot && this.cardUnitFakeMarkup !== null) {
          this.cardUnitSlot.innerHTML = this.cardUnitFakeMarkup;
        }
        this.showDeactivatedState();
      }

      showActivatedState() {
        this.offLabel?.setAttribute('hidden', '');
        this.onLabel?.removeAttribute('hidden');
      }

      showDeactivatedState() {
        this.onLabel?.setAttribute('hidden', '');
        this.offLabel?.removeAttribute('hidden');
      }
    }
  );
}
